import { SEBBIA_ISOTIPO_PATH, SEBBIA_ISOTIPO_VIEWBOX } from "@/lib/brandMarks";
import { cn } from "@/lib/utils";

/**
 * Isotipo SEBBIA (el rayo), sin contenedor.
 *
 * El path se pinta con `fill="currentColor"`, así que se tiñe con cualquier clase
 * `text-*` — la misma pieza sirve dorada sobre fondo oscuro, blanca sobre el hero
 * indigo y negra sólida en impresos.
 *
 * La geometría NO se importa del `.svg`: vive en `lib/brandMarks.js` porque los
 * documentos de impresión (ventanas `about:blank`, sin bundler) necesitan el mismo
 * trazo como string. Una sola fuente para pantalla y papel.
 */
export const SebbiaIsotipo = ({ className, ...props }) => (
  <svg
    viewBox={SEBBIA_ISOTIPO_VIEWBOX}
    className={cn("h-full w-auto", className)}
    aria-hidden="true"
    {...props}
  >
    <path fill="currentColor" d={SEBBIA_ISOTIPO_PATH} />
  </svg>
);

/**
 * Marca SEBBIA en su contenedor: rayo dorado sobre indigo-900.
 *
 * Reproduce a propósito la composición exacta del favicon (rayo al 81% del
 * alto del cuadro) para que la pestaña del navegador, el login y el badge de
 * la app se lean como la misma pieza y no como tres dibujos parecidos.
 *
 * OJO: esta es la marca de la *plataforma*. El logo de cada agencia cliente
 * es `branding.logo_url` y se renderiza aparte en AppShell — no mezclar.
 */
export const SebbiaMark = ({ className, iconClassName }) => (
  <div
    className={cn(
      "inline-flex shrink-0 items-center justify-center rounded-2xl bg-indigo-900",
      className,
    )}
  >
    <SebbiaIsotipo className={cn("h-[81%] text-gold-600", iconClassName)} />
  </div>
);

/**
 * La palabra de la marca.
 *
 * Es una constante del kit, no un dato: en la app el wordmark se dibuja con Manrope
 * extrabold, así que "el logo" y "el texto" son la misma cosa. Importarla desde acá
 * (en vez de leer `branding.brand_name`) es lo que hace que la plataforma se presente
 * con su marca sin depender de que alguien haya escrito "SEBBIA" en Configuración.
 */
export const SEBBIA_WORDMARK = "SEBBIA";

const LOCKUP_SIZES = {
  sm: { mark: "h-8 w-8 rounded-xl", icon: "h-4", word: "text-base", sub: "text-[10px]" },
  md: { mark: "h-10 w-10", icon: "h-5", word: "text-xl", sub: "text-[11px]" },
  lg: { mark: "h-12 w-12", icon: "h-6", word: "text-2xl", sub: "text-xs" },
};

/**
 * Lockup SEBBIA: isotipo + palabra, con subtítulo opcional.
 *
 * Resuelve el hueco del kit de marca original: las tres imágenes entregadas por
 * el diseñador están armadas sobre fondo oscuro y su wordmark plateado desaparece
 * sobre el `#F8FAFC` de la app. Acá la palabra es tipografía (Manrope extrabold),
 * no una imagen, así que hereda el color del contexto y se lee en cualquier fondo
 * — incluido el monocromo de un impreso.
 *
 * - `variant="tile"`  → el rayo dorado sobre su cuadro indigo (`SebbiaMark`).
 *    El cuadro trae su propio fondo, así que funciona sobre claro y sobre oscuro.
 * - `variant="plain"` → el rayo pelado en `currentColor`: para fondos que ya
 *    tienen color propio o para el monocromo de impresión.
 */
export const SebbiaLockup = ({
  className,
  variant = "tile",
  size = "md",
  subtitle,
  wordClassName,
  subtitleClassName,
  as: Word = "p",
}) => {
  const s = LOCKUP_SIZES[size] || LOCKUP_SIZES.md;
  return (
    <div className={cn("flex items-center gap-3", className)}>
      {variant === "tile" ? (
        <SebbiaMark className={s.mark} />
      ) : (
        <SebbiaIsotipo className={cn(s.icon, "w-auto")} />
      )}
      <div className="leading-tight">
        <Word className={cn("font-display font-extrabold tracking-tight", s.word, wordClassName)}>
          {SEBBIA_WORDMARK}
        </Word>
        {subtitle ? (
          <p
            className={cn(
              "font-semibold uppercase tracking-widest text-slate-400",
              s.sub,
              subtitleClassName,
            )}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
};
